import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, LogOut, Menu, X, LayoutDashboard, Package, ClipboardList, Shield, Heart, Home, Store, Search, Bell, CheckCheck, PhoneCall } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useBranding } from '../context/BrandingContext';
import API from '../utils/api';
import { playNotificationSound } from '../utils/notificationSound';
import { toast } from 'react-toastify';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wCount } = useWishlist();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropRef = useRef(null);
  const notifRef = useRef(null);
  const menuRef = useRef(null);
  const brandLogoSrc = branding.logoImage || '/material-stock-logo.jpg';

  // Close dropdown and menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); setDropOpen(false); setMenuOpen(false); };

  const loadNotifications = async (playNewSound = false) => {
    if (!user) return;
    try {
      const res = await API.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnread(prev => {
        const next = Number(res.data.unread || 0);
        if (playNewSound && next > prev) playNotificationSound('info');
        return next;
      });
    } catch {
      // Keep nav quiet if the notification API is temporarily unavailable.
    }
  };

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnread(0);
      return undefined;
    }
    loadNotifications(false);
    const timer = setInterval(() => loadNotifications(true), 30000);
    return () => clearInterval(timer);
  }, [user?._id]);

  const markAllRead = async () => {
    try {
      await API.put('/notifications/read-all');
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const openNotification = async (notification) => {
    try {
      if (!notification.read) {
        await API.put(`/notifications/${notification._id}/read`);
        setUnread(prev => Math.max(0, prev - 1));
      }
    } catch {}
    setNotifOpen(false);
    if (notification.link) navigate(notification.link);
  };

  const ownerLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/owner/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/owner/dashboard?tab=orders', label: 'Orders', icon: ClipboardList },
    { to: '/owner/materials', label: 'My Materials', icon: Package },
    { to: '/contact', label: 'Contact Us', icon: PhoneCall },
  ];
  const adminLinks = [
    { to: '/admin', label: 'Admin Panel', icon: Shield },
    { to: '/contact', label: 'Contact Us', icon: PhoneCall },
  ];
  const customerLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/shops', label: 'Find Shops', icon: Store },
    { to: '/shops?view=materials', label: 'Materials', icon: Package },
    { to: '/orders', label: 'My Orders', icon: ClipboardList },
    { to: '/contact', label: 'Contact Us', icon: PhoneCall },
  ];
  const guestLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/shops', label: 'Find Shops', icon: Store },
    { to: '/shops?view=materials', label: 'Materials', icon: Package },
    { to: '/contact', label: 'Contact Us', icon: PhoneCall },
  ];

  const navLinks = user?.role === 'owner' ? ownerLinks : user?.role === 'admin' ? adminLinks : user?.role === 'customer' ? customerLinks : guestLinks;
  const accountPath = user?.role === 'owner' ? '/owner/dashboard' : user?.role === 'admin' ? '/admin' : '/dashboard';
  const isActive = (path) => {
    const [pathname, search = ''] = path.split('?');
    if (location.pathname !== pathname) return false;
    if (!search) return !location.search.includes('view=materials');
    return new URLSearchParams(location.search).get('view') === new URLSearchParams(search).get('view');
  };

  return (
    <>
      <nav style={{ background: 'white', borderBottom: '1px solid var(--gray-border)', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>

          {/* Logo */}
          <Link to="/" className="site-brand-lockup" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img
              src={brandLogoSrc}
              alt={branding.appName || 'Material Stock'}
              className="site-brand-logo"
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="nav-links" style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'center' }}>
            {navLinks.map(link => (
              <Link key={link.to} to={link.to}
                onClick={(e) => {
                  if (!user && link.requireAuth) {
                    e.preventDefault();
                    toast.info(link.prompt);
                    navigate('/login');
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, color: isActive(link.to) ? 'var(--orange)' : 'var(--gray)', fontSize: 13, fontWeight: 500, background: isActive(link.to) ? 'var(--orange-soft)' : 'transparent', transition: 'all 0.2s' }}>
                <link.icon size={14} />{link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {/* Wishlist */}
            {user && (
              <Link
                to="/wishlist"
                className={user?.role === 'customer' ? 'nav-mobile-redundant' : ''}
                style={{ position: 'relative', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: 'var(--gray-light)', color: 'var(--dark)' }}
              >
                <Heart size={18} />
                {wCount > 0 && <span style={{ position: 'absolute', top: -3, right: -3, background: 'var(--red)', color: 'white', borderRadius: '50%', width: 17, height: 17, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{wCount}</span>}
              </Link>
            )}

            {/* Cart */}
            {user?.role === 'customer' && (
              <Link
                to="/cart"
                className="nav-mobile-redundant"
                style={{ position: 'relative', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: 'var(--gray-light)', color: 'var(--dark)' }}
              >
                <ShoppingCart size={18} />
                {count > 0 && <span style={{ position: 'absolute', top: -3, right: -3, background: 'var(--orange)', color: 'white', borderRadius: '50%', width: 17, height: 17, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>}
              </Link>
            )}

            {/* Notifications */}
            {user && (
              <div style={{ position: 'relative' }} ref={notifRef}>
                <button
                  onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) loadNotifications(false); }}
                  title="Notifications"
                  aria-label="Notifications"
                  style={{ position: 'relative', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: unread > 0 ? 'var(--orange-soft)' : 'var(--gray-light)', color: unread > 0 ? 'var(--orange)' : 'var(--dark)' }}
                >
                  <Bell size={18} />
                  {unread > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -4, background: 'var(--red)', color: 'white', borderRadius: 10, minWidth: 18, height: 18, padding: '0 5px', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div style={{ position: 'absolute', right: -44, top: '115%', width: 'min(360px, calc(100vw - 24px))', background: 'white', border: '1px solid var(--gray-border)', borderRadius: 14, boxShadow: 'var(--shadow-lg)', zIndex: 250, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px', background: 'var(--gray-light)', borderBottom: '1px solid var(--gray-border)' }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>Notifications</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11 }}>{unread} unread</div>
                      </div>
                      <button onClick={markAllRead} title="Mark all read" style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'white', border: '1px solid var(--gray-border)', borderRadius: 8, padding: '6px 8px', color: 'var(--gray)', fontWeight: 700, fontSize: 11 }}>
                        <CheckCheck size={13} /> Read all
                      </button>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>
                          No notifications yet
                        </div>
                      ) : notifications.map(n => (
                        <button
                          key={n._id}
                          onClick={() => openNotification(n)}
                          style={{ width: '100%', display: 'grid', gridTemplateColumns: '9px minmax(0,1fr)', gap: 10, textAlign: 'left', padding: '12px 14px', background: n.read ? 'white' : 'var(--orange-softer)', border: 'none', borderBottom: '1px solid var(--gray-border)', cursor: 'pointer' }}
                        >
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: n.read ? 'var(--gray-border)' : 'var(--orange)', marginTop: 5 }} />
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                              <strong style={{ fontSize: 13, color: 'var(--dark)' }}>{n.title}</strong>
                              <span style={{ color: 'var(--gray)', fontSize: 10, whiteSpace: 'nowrap' }}>{new Date(n.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                            </span>
                            <span style={{ display: 'block', color: 'var(--gray)', fontSize: 12, lineHeight: 1.4 }}>{n.message}</span>
                            <span style={{ display: 'inline-block', marginTop: 6, color: 'var(--orange)', background: 'white', border: '1px solid var(--orange-border)', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 800, textTransform: 'capitalize' }}>
                              {n.type}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User dropdown */}
            {user ? (
              <div className="nav-user-menu" style={{ position: 'relative' }} ref={dropRef}>
                <button onClick={() => setDropOpen(!dropOpen)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13 }}>{user.name[0].toUpperCase()}</div>
                  <span className="hide-mobile" style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name.split(' ')[0]}</span>
                </button>
                {dropOpen && (
                  <div style={{ position: 'absolute', right: 0, top: '110%', background: 'white', border: '1px solid var(--gray-border)', borderRadius: 12, minWidth: 190, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', zIndex: 200 }}>
                    <div style={{ padding: '12px 14px', background: 'var(--gray-light)' }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{user.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray)' }}>{user.email}</div>
                      <span className="badge badge-orange" style={{ fontSize: 10, marginTop: 4, display: 'inline-block', textTransform: 'capitalize' }}>{user.role}</span>
                    </div>
                    <Link to={accountPath} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', fontSize: 13, color: 'var(--dark)', textDecoration: 'none' }} onClick={() => setDropOpen(false)}>
                      <LayoutDashboard size={14} /> My Account
                    </Link>
                    <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', width: '100%', background: 'none', border: 'none', cursor: 'pointer', borderTop: '1px solid var(--gray-border)' }}>
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-btns" style={{ display: 'flex', gap: 6 }}>
                <Link to="/login" className="btn-outline" style={{ padding: '7px 14px', fontSize: 12 }}>Login</Link>
                <Link to="/register" className="btn-primary" style={{ padding: '7px 14px', fontSize: 12 }}>Sign Up</Link>
              </div>
            )}

            {/* Mobile hamburger */}
            <button className="nav-menu-btn" onClick={() => setMenuOpen(!menuOpen)} style={{ display: 'none', background: 'none', border: 'none', color: 'var(--dark)', padding: 4, cursor: 'pointer' }}>
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Right Sidebar Drawer */}
        {menuOpen && (
          <>
            <div className="mobile-sidebar-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="mobile-sidebar-drawer" ref={menuRef}>
              <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--gray-border)', background: 'white', flexShrink: 0 }}>
                <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={brandLogoSrc} alt={branding.appName || 'Material Stock'} style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
                </Link>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  style={{ background: 'var(--gray-light)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dark)', cursor: 'pointer' }}
                >
                  <X size={18} />
                </button>
              </div>

              <div style={{ padding: '16px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {user && (
                  <div style={{ padding: '12px 14px', marginBottom: 10, background: 'var(--gray-light)', borderRadius: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--dark)' }}>{user.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 1 }}>{user.email}</div>
                    <span className="badge badge-orange" style={{ fontSize: 10, marginTop: 6, display: 'inline-block', textTransform: 'capitalize' }}>{user.role}</span>
                  </div>
                )}

                {user && (
                  <Link to={accountPath} onClick={() => setMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, fontSize: 14, color: isActive(accountPath) ? 'var(--orange)' : 'var(--dark)', fontWeight: 600, background: isActive(accountPath) ? 'var(--orange-soft)' : 'transparent', marginBottom: 4 }}>
                    <LayoutDashboard size={18} /> My Account
                  </Link>
                )}

                {navLinks.filter(link => link.to !== accountPath).map(link => (
                  <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, fontSize: 14, color: isActive(link.to) ? 'var(--orange)' : 'var(--dark)', fontWeight: 500, background: isActive(link.to) ? 'var(--orange-soft)' : 'transparent', marginBottom: 2 }}>
                    <link.icon size={18} /> {link.label}
                  </Link>
                ))}

                {user && (
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      borderRadius: 10,
                      fontSize: 14,
                      color: 'var(--red)',
                      fontWeight: 600,
                      background: '#FEE2E2',
                      border: 'none',
                      cursor: 'pointer',
                      marginTop: 6,
                      width: '100%'
                    }}
                  >
                    <LogOut size={18} /> Logout
                  </button>
                )}
              </div>

              {!user && (
                <div style={{ padding: '16px 16px max(24px, calc(env(safe-area-inset-bottom) + 16px)) 16px', borderTop: '1px solid var(--gray-border)', background: 'white', flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Link to="/login" onClick={() => setMenuOpen(false)} className="btn-outline" style={{ justifyContent: 'center', padding: '11px' }}>Login</Link>
                    <Link to="/register" onClick={() => setMenuOpen(false)} className="btn-primary" style={{ justifyContent: 'center', padding: '11px' }}>Sign Up</Link>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </nav>

      {/* Mobile Bottom Nav - for customers and guest users (without login) */}
      {(!user || user?.role === 'customer') && !menuOpen && (
        <div className="mobile-bottom-nav">
          {[
            { to: '/', icon: Home, label: 'Home' },
            { to: '/shops', icon: Search, label: 'Search' },
            { to: '/wishlist', icon: Heart, label: 'Wishlist', badge: wCount },
            { to: '/cart', icon: ShoppingCart, label: 'Cart', badge: user ? count : 0, requireAuth: true, prompt: 'Please login to view your cart' },
            { to: '/orders', icon: ClipboardList, label: 'Orders', requireAuth: true, prompt: 'Please login to view your orders' },
          ].map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={(e) => {
                if (!user && item.requireAuth) {
                  e.preventDefault();
                  toast.info(item.prompt);
                  navigate('/login');
                }
              }}
              className={`mobile-nav-item ${isActive(item.to) ? 'active' : ''}`}
            >
              <div style={{ position: 'relative' }}>
                <item.icon size={20} />
                {item.badge > 0 && (
                  <span style={{ position: 'absolute', top: -6, right: -6, background: item.to === '/wishlist' ? 'var(--red)' : 'var(--orange)', color: 'white', borderRadius: '50%', width: 15, height: 15, fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.badge}
                  </span>
                )}
              </div>
              {item.label}
            </Link>
          ))}
        </div>
      )}

      {/* Mobile Bottom Nav - for shop owners */}
      {user?.role === 'owner' && !menuOpen && (
        <div className="mobile-bottom-nav">
          {[
            { to: '/', icon: Home, label: 'Home' },
            { to: '/owner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { to: '/owner/dashboard?tab=orders', icon: ClipboardList, label: 'Orders' },
            { to: '/owner/materials', icon: Package, label: 'Materials' },
          ].map(item => (
            <Link key={item.to} to={item.to} className={`mobile-nav-item ${isActive(item.to) ? 'active' : ''}`}>
              <item.icon size={20} />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
